import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  Stack,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, AttachMoneyOutlined as DollarIcon } from '@mui/icons-material';
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable, MRT_Row, MRT_TableInstance, MRT_Cell } from 'material-react-table';
import { ProjectPreviousFundingDTO } from './project.type';
import { formatCurrency, formatISODate } from '../../common/utils/common.util';
import { getLatestAdjustmentApplicationNumber } from './project.util';
import WhiteFab from '../common/WhiteFab';
import { useMRTStyling } from '../../hooks/useMRTStyling';
import { REPORT_COLORS } from '../report/constants';
import DynamicDatePicker from '../common/DynamicDatePicker';

interface ProjectPreviousFundingsTableProps {
  previousFundings?: ProjectPreviousFundingDTO[];
  isEditMode?: boolean;
  onChange?: (updatedFundings: ProjectPreviousFundingDTO[]) => void;
  readonly?: boolean;
  isMagicReport?: boolean;
  isDirty?: boolean;
  onHoveredFundingId?: (id: string | null) => void;
  /** Stretches the card to the height of its container, for side-by-side layouts. */
  fullHeight?: boolean;
}

const sortFundings = (fundings?: ProjectPreviousFundingDTO[]) => {
  if (!fundings || fundings.length === 0) return [];
  return [...fundings].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
};

const isFundingEmpty = (funding: ProjectPreviousFundingDTO): boolean => {
  const hasApplicationNumber =
    funding.applicationNumber !== undefined && funding.applicationNumber !== null && funding.applicationNumber.trim() !== '';
  const hasApplicationDate =
    funding.applicationDate !== undefined &&
    funding.applicationDate !== null &&
    typeof funding.applicationDate === 'string' &&
    funding.applicationDate.trim() !== '';
  const hasAmountFunded = funding.amountFunded !== undefined && funding.amountFunded !== null && funding.amountFunded !== 0;
  const hasReceivedByContractor =
    funding.receivedByContractor !== undefined && funding.receivedByContractor !== null && funding.receivedByContractor !== 0;

  // An entry is empty if all tracked fields are empty
  return !hasApplicationNumber && !hasApplicationDate && !hasAmountFunded && !hasReceivedByContractor;
};

const filterEmptyFundings = (fundings: ProjectPreviousFundingDTO[]): ProjectPreviousFundingDTO[] => {
  return fundings.filter(funding => !isFundingEmpty(funding));
};

type NewFundingFormState = {
  applicationNumber: string;
  applicationDate: string | null;
  amountFunded: string;
  receivedByContractor: string;
  comment: string;
  isAdjustment: boolean;
};

const createEmptyFundingFormState = (): NewFundingFormState => ({
  isAdjustment: false,
  applicationNumber: '',
  applicationDate: null,
  amountFunded: '',
  receivedByContractor: '',
  comment: '',
});

const ProjectPreviousFundingsTable: React.FC<ProjectPreviousFundingsTableProps> = ({
  previousFundings,
  onChange,
  readonly,
  isMagicReport = false,
  isDirty,
  onHoveredFundingId,
  fullHeight = false,
}) => {
  const [tableData, setTableData] = useState<ProjectPreviousFundingDTO[]>(sortFundings(previousFundings));
  const [editedFundingIds, setEditedFundingIds] = useState<Set<string>>(new Set());
  const [createdFundingIds, setCreatedFundingIds] = useState<Set<string>>(new Set());
  const [prevIsDirty, setPrevIsDirty] = useState<boolean | undefined>(isDirty);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newFundingForm, setNewFundingForm] = useState<NewFundingFormState>(createEmptyFundingFormState);
  const isInternalChangeRef = React.useRef(false);
  const theme = useTheme();
  const { commonMRTStyling, getActionsColumnStyling } = useMRTStyling<ProjectPreviousFundingDTO>();
  const actionsColumnStyling = getActionsColumnStyling();

  useEffect(() => {
    // Skip reset if this change came from within the component
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    const sorted = (sortFundings(previousFundings) || []).map(funding => ({
      ...funding,
      reviewedByCDCC: funding.reviewedByCDCC ?? true, // Ensure default value
      comment: funding.comment ?? undefined,
    }));
    setTableData(prevTableData => {
      const currentDataIds = new Set(sorted.map(f => f.id));
      const existingDataIds = new Set(prevTableData.map(f => f.id));

      // Only reset tracking if the data structure actually changed externally
      // (e.g., different IDs or different number of items)
      const idsChanged = currentDataIds.size !== existingDataIds.size || Array.from(currentDataIds).some(id => !existingDataIds.has(id));

      if (idsChanged) {
        // Reset edited/created tracking only when data structure changes externally
        setEditedFundingIds(new Set());
        setCreatedFundingIds(new Set());
      } else {
        // If data structure hasn't changed but data has been updated externally (e.g., after save),
        // clear the edited/created tracking to remove yellow background
        const dataChanged = JSON.stringify(sorted) !== JSON.stringify(prevTableData);
        if (dataChanged) {
          setEditedFundingIds(new Set());
          setCreatedFundingIds(new Set());
        }
      }

      return sorted;
    });
  }, [previousFundings]);

  // Clear tracking when save completes (isDirty changes from true to false)
  useEffect(() => {
    if (prevIsDirty === true && isDirty === false) {
      // Save just completed - clear all tracking to remove yellow backgrounds
      setEditedFundingIds(new Set());
      setCreatedFundingIds(new Set());
    }
    setPrevIsDirty(isDirty);
  }, [isDirty, prevIsDirty]);

  const allowEditing = !readonly;

  const isAddEntryDisabled = useMemo(() => {
    if (!newFundingForm.applicationNumber.trim()) {
      return true;
    }

    if (newFundingForm.amountFunded.trim() !== '' && !Number.isFinite(Number(newFundingForm.amountFunded))) {
      return true;
    }

    // Adjustment rows exist only to carry a Received by Contractor difference
    if (
      newFundingForm.isAdjustment &&
      (newFundingForm.receivedByContractor.trim() === '' || !Number(newFundingForm.receivedByContractor))
    ) {
      return true;
    }

    return false;
  }, [newFundingForm]);

  const isDiscrepancyUnresolved = useMemo(() => {
    if (!tableData.some(funding => funding.isDiscrepancy)) return false;
    const fundedByLenderSum = tableData.reduce((sum, funding) => sum + (funding.amountFunded || 0), 0);
    const receivedByContractorSum = tableData.reduce((sum, funding) => sum + (funding.receivedByContractor || 0), 0);
    return Math.abs(fundedByLenderSum - receivedByContractorSum) >= 0.01;
  }, [tableData]);

  const getDiscrepancyStripePattern = (baseColor: string) =>
    `repeating-linear-gradient(45deg, ${baseColor}, ${baseColor} 3px, ${REPORT_COLORS.DISCREPANCY_BACKGROUND_COLOR} 3px, ${REPORT_COLORS.DISCREPANCY_BACKGROUND_COLOR} 6px)`;

  const getRowBackground = useCallback(
    (funding: ProjectPreviousFundingDTO, rowIndex: number): string => {
      const zebraColor = rowIndex % 2 === 0 ? '#f9f9f9' : '#ffffff';
      const isCreated = createdFundingIds.has(funding.id);
      const isEdited = editedFundingIds.has(funding.id);
      if (isCreated || isEdited) return REPORT_COLORS.UNSAVED_BACKGROUND_COLOR;
      if (funding.isDiscrepancy && isDiscrepancyUnresolved) return getDiscrepancyStripePattern(zebraColor);
      return zebraColor;
    },
    [createdFundingIds, editedFundingIds, isDiscrepancyUnresolved],
  );

  const openAddEntryDialog = () => {
    if (!allowEditing) return;
    setNewFundingForm(createEmptyFundingFormState());
    setIsAddDialogOpen(true);
  };

  const handleCloseAddEntryDialog = () => {
    setIsAddDialogOpen(false);
    setNewFundingForm(createEmptyFundingFormState());
  };

  const handleAdjustmentToggle = (checked: boolean) => {
    setNewFundingForm(prev => ({
      ...prev,
      isAdjustment: checked,
      applicationNumber: checked ? getLatestAdjustmentApplicationNumber(tableData) : '',
      amountFunded: checked ? '0' : '',
    }));
  };

  const handleConfirmAddEntry = () => {
    if (!allowEditing || isAddEntryDisabled) {
      return;
    }

    const amountFundedValue =
      !newFundingForm.isAdjustment && newFundingForm.amountFunded.trim() !== '' ? Number(newFundingForm.amountFunded) : 0;

    if (!Number.isFinite(amountFundedValue)) {
      return;
    }

    const receivedByContractorValue = newFundingForm.receivedByContractor !== '' ? Number(newFundingForm.receivedByContractor) : undefined;

    const newFunding: ProjectPreviousFundingDTO = {
      id: crypto.randomUUID(),
      displayOrder: tableData.length + 1,
      applicationNumber: newFundingForm.applicationNumber.trim() || undefined,
      applicationDate: newFundingForm.applicationDate || undefined,
      amountFunded: amountFundedValue,
      receivedByContractor: Number.isFinite(receivedByContractorValue) ? receivedByContractorValue : undefined,
      approvedAmount: 0,
      retainage: 0,
      percentRequested: 0,
      percentComplete: 0,
      totalContractAmount: 0,
      comment: newFundingForm.comment.trim() || undefined,
      reviewedByCDCC: !newFundingForm.isAdjustment, // Default to reviewed; adjustments match the auto-generated rows
      ...(newFundingForm.isAdjustment && { isDiscrepancy: true }),
    };

    const updatedData = [...tableData, newFunding];
    setTableData(updatedData);
    setCreatedFundingIds(prevIds => {
      const newSet = new Set(prevIds);
      newSet.add(newFunding.id);
      return newSet;
    });
    isInternalChangeRef.current = true;

    const filteredData = filterEmptyFundings(updatedData);
    const reorderedData = filteredData.map((funding, idx) => ({
      ...funding,
      displayOrder: idx + 1,
    }));
    onChange?.(reorderedData);

    setIsAddDialogOpen(false);
    setNewFundingForm(createEmptyFundingFormState());
  };

  const updateFundingField = useCallback(
    <K extends keyof ProjectPreviousFundingDTO>(rowIndex: number, field: K, value: ProjectPreviousFundingDTO[K]) => {
      if (!allowEditing) return;
      setTableData(prev => {
        const oldFunding = prev[rowIndex];
        const oldValue = oldFunding[field];

        // Normalize values for comparison (handle null, empty string as undefined)
        const normalizeForComparison = (val: any, isDateField: boolean, isBooleanField: boolean): any => {
          if (val === null || val === '') return undefined;
          if (typeof val === 'number' && isNaN(val)) return undefined;

          // For boolean fields, normalize undefined to default value (true for reviewedByCDCC)
          if (isBooleanField && val === undefined) {
            return field === 'reviewedByCDCC' ? true : val;
          }

          // For date fields, normalize to YYYY-MM-DD format for comparison
          if (isDateField && typeof val === 'string' && val.trim() !== '') {
            // Extract just the date part (YYYY-MM-DD) from ISO strings or use as-is if already in YYYY-MM-DD format
            const datePart = val.includes('T') ? val.split('T')[0] : val;
            // Validate it's in YYYY-MM-DD format
            if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
              return datePart;
            }
            // If not in expected format, return as-is for comparison (will likely be different)
            return val;
          }

          return val;
        };

        // Check if this is a date field or boolean field
        const isDateField = field === 'applicationDate';
        const isBooleanField = field === 'reviewedByCDCC';

        const normalizedOld = normalizeForComparison(oldValue, isDateField, isBooleanField);
        const normalizedNew = normalizeForComparison(value, isDateField, isBooleanField);

        // Compare normalized values using strict equality
        const hasChanged = normalizedOld !== normalizedNew;

        // Only update if the value actually changed
        if (!hasChanged) {
          return prev;
        }

        const updated = prev.map((funding, idx) => (idx === rowIndex ? { ...funding, [field]: value } : funding));
        // Mark the row as edited if it's not a newly created row
        const fundingId = prev[rowIndex].id;
        if (!createdFundingIds.has(fundingId)) {
          setEditedFundingIds(prevIds => {
            const newSet = new Set(prevIds);
            newSet.add(fundingId);
            return newSet;
          });
        }
        // Mark that this is an internal change to prevent reset in useEffect
        isInternalChangeRef.current = true;
        // Filter out empty entries before calling onChange to prevent saving empty entries
        const filteredUpdated = filterEmptyFundings(updated);
        // Update displayOrder for filtered entries
        const reorderedUpdated = filteredUpdated.map((funding, idx) => ({
          ...funding,
          displayOrder: idx + 1,
        }));
        onChange?.(reorderedUpdated);
        return updated;
      });
    },
    [allowEditing, onChange, createdFundingIds],
  );

  const columns = useMemo<MRT_ColumnDef<ProjectPreviousFundingDTO>[]>(
    () => [
      {
        header: 'App #',
        accessorKey: 'applicationNumber',
        // layoutMode 'grid' uses size as the column's flex-basis (MRT default is 180).
        size: 90,
        muiTableBodyCellProps: ({ row }) => {
          const rowBg = getRowBackground(row.original, row.index);

          return {
            sx: {
              textAlign: 'right',
              border: 'none',
              background: `${rowBg} !important`,
              cursor: allowEditing && isMagicReport ? 'pointer' : 'default',
            },
            onClick: (e: React.MouseEvent) => {
              if (allowEditing && isMagicReport) {
                e.stopPropagation();
                const currentValue = row.original.reviewedByCDCC ?? true;
                updateFundingField(row.index, 'reviewedByCDCC', !currentValue);
              }
            },
          };
        },
        Cell: ({ cell, row }: { cell: MRT_Cell<ProjectPreviousFundingDTO>; row: MRT_Row<ProjectPreviousFundingDTO> }) => {
          const value = cell.getValue<string | undefined>() ?? '--';
          const hasAsterisk = isMagicReport && !(row.original.reviewedByCDCC ?? true);
          if (hasAsterisk) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'primary.main' }}>*</Typography>
                <span>{value}</span>
              </Box>
            );
          }
          return <span>{value}</span>;
        },
        muiEditTextFieldProps: ({ row }) => ({
          type: 'text',
          onBlur: event => updateFundingField(row.index, 'applicationNumber', event.target.value.trim() || undefined),
        }),
        Footer: () => (
          <Typography variant='button' fontWeight='bold'>
            Totals
          </Typography>
        ),
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            textAlign: 'right',
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            // Sticky footer cell background via ::before
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            // Maintain stickiness & layer above ::before
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
      },
      {
        header: 'Application Date',
        accessorKey: 'applicationDate',
        size: 120,
        muiTableBodyCellProps: ({ row }) => {
          const rowBg = getRowBackground(row.original, row.index);

          return {
            sx: {
              border: 'none',
              background: `${rowBg} !important`,
            },
          };
        },
        Cell: ({ cell }) => {
          const value = cell.getValue<string | undefined>();
          return value ? formatISODate(value) : '--';
        },
        Edit: ({ cell, table }: { cell: MRT_Cell<ProjectPreviousFundingDTO>; table: MRT_TableInstance<ProjectPreviousFundingDTO> }) => {
          const currentValue = cell.getValue<string | undefined>();

          return (
            <Box sx={{ width: '100%', minWidth: '150px' }}>
              <DynamicDatePicker
                fieldName='applicationDate'
                value={currentValue || null}
                onChange={(field, value) => {
                  updateFundingField(cell.row.index, 'applicationDate', value || undefined);
                }}
              />
            </Box>
          );
        },
        Footer: () => '',
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            // Sticky footer cell background via ::before
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            // Maintain stickiness & layer above ::before
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
      },
      {
        header: 'Received by Contractor',
        accessorKey: 'receivedByContractor',
        size: 140,
        muiTableBodyCellProps: ({ row }) => {
          const rowBg = getRowBackground(row.original, row.index);

          return {
            sx: {
              textAlign: 'right',
              border: 'none',
              background: `${rowBg} !important`,
            },
          };
        },
        Cell: ({ cell }) => formatCurrency((cell.getValue<number | undefined>() ?? 0).toString(), true, false, true),
        muiEditTextFieldProps: ({ row }) => ({
          type: 'number',
          inputProps: { step: 0.01 },
          onBlur: event =>
            updateFundingField(row.index, 'receivedByContractor', event.target.value !== '' ? Number(event.target.value) : undefined),
        }),
        Footer: () => {
          const total = tableData.reduce((sum, funding) => sum + (funding.receivedByContractor || 0), 0);

          return (
            <Typography variant='button' fontWeight='bold'>
              {formatCurrency(total.toString(), true, false, true)}
            </Typography>
          );
        },
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            textAlign: 'right',
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            // Sticky footer cell background via ::before
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            // Maintain stickiness & layer above ::before
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
      },
      {
        header: 'Funded by Lender',
        accessorKey: 'amountFunded',
        size: 140,
        muiTableBodyCellProps: ({ row }) => {
          const rowBg = getRowBackground(row.original, row.index);

          return {
            sx: {
              textAlign: 'right',
              border: 'none',
              background: `${rowBg} !important`,
            },
          };
        },
        Cell: ({ cell }) => formatCurrency((cell.getValue<number | undefined>() ?? 0).toString(), true, false, true),
        muiEditTextFieldProps: ({ row }) => ({
          type: 'number',
          inputProps: { step: 0.01 },
          onBlur: event =>
            updateFundingField(row.index, 'amountFunded', event.target.value !== '' ? Number(event.target.value) : undefined),
        }),
        Footer: () => {
          const total = tableData.reduce((sum, funding) => sum + (funding.amountFunded || 0), 0);
          return (
            <Typography variant='button' fontWeight='bold'>
              {formatCurrency(total.toString(), true, false, true)}
            </Typography>
          );
        },
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            textAlign: 'right',
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            // Sticky footer cell background via ::before
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            // Maintain stickiness & layer above ::before
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
      },
      {
        header: 'Internal Comment',
        accessorKey: 'comment',
        muiTableBodyCellProps: ({ row }) => {
          const rowBg = getRowBackground(row.original, row.index);

          return {
            sx: {
              border: 'none',
              background: `${rowBg} !important`,
            },
          };
        },
        Cell: ({ cell }) => {
          const value = cell.getValue<string | undefined>();
          return value || '--';
        },
        muiEditTextFieldProps: ({ row }) => ({
          type: 'text',
          multiline: true,
          onBlur: event => updateFundingField(row.index, 'comment', event.target.value.trim() || undefined),
        }),
        Footer: () => '',
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            // Sticky footer cell background via ::before
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            // Maintain stickiness & layer above ::before
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
      },
    ],
    [tableData, updateFundingField, createdFundingIds, editedFundingIds, theme],
  );

  const handleDeleteRow = useCallback(
    (row: MRT_Row<ProjectPreviousFundingDTO>) => {
      if (!allowEditing) return;
      const fundingId = row.original.id;
      const updatedData = tableData
        .filter((_, idx) => idx !== row.index)
        .map((funding, idx) => ({
          ...funding,
          displayOrder: idx + 1,
        }));
      setTableData(updatedData);
      // Remove from edited/created tracking
      setEditedFundingIds(prevIds => {
        const newIds = new Set(prevIds);
        newIds.delete(fundingId);
        return newIds;
      });
      setCreatedFundingIds(prevIds => {
        const newIds = new Set(prevIds);
        newIds.delete(fundingId);
        return newIds;
      });
      // Mark that this is an internal change to prevent reset in useEffect
      isInternalChangeRef.current = true;
      // Filter out empty entries before calling onChange to prevent saving empty entries
      const filteredData = filterEmptyFundings(updatedData);
      // Update displayOrder for filtered entries
      const reorderedData = filteredData.map((funding, idx) => ({
        ...funding,
        displayOrder: idx + 1,
      }));
      onChange?.(reorderedData);
    },
    [allowEditing, onChange, tableData],
  );

  const table = useMaterialReactTable({
    ...commonMRTStyling,
    columns,
    data: tableData,
    layoutMode: 'grid',
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableDensityToggle: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowActions: allowEditing,
    enableRowSelection: false,
    enableEditing: allowEditing,
    editDisplayMode: 'cell',
    getRowId: row => row.id,
    enableStickyFooter: true,
    state: {
      showAlertBanner: !tableData.length,
    },
    initialState: {
      density: 'compact',
      columnPinning: { right: ['mrt-row-actions'] },
    },
    enableColumnPinning: true,
    renderRowActions: allowEditing
      ? ({ row }) => {
          return (
            <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
              <Tooltip title='Delete entry' arrow={false}>
                <span>
                  <WhiteFab
                    size='small'
                    sx={{ width: 32, height: 32, minHeight: 32 }}
                    icon={<DeleteIcon fontSize='small' />}
                    textColor='error'
                    onClick={() => handleDeleteRow(row)}
                  />
                </span>
              </Tooltip>
            </Stack>
          );
        }
      : undefined,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 50,
        ...actionsColumnStyling,
        muiTableHeadCellProps: {
          sx: (theme: any) => ({
            ...((actionsColumnStyling.muiTableHeadCellProps as any)?.sx ?? {}),
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff',
          }),
        },
        Header: () => null,
        muiTableBodyCellProps: ({ row }) => {
          const rowBg = getRowBackground(row.original, row.index);

          return {
            sx: {
              border: 'none',
              background: `${rowBg} !important`,
              '&[data-pinned="true"]::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `${rowBg} !important`,
                zIndex: -1,
              },
            },
          };
        },
        Footer: () => '',
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            border: 'none',
            fontSize: '0.9rem',
            padding: '16px 12px',
            // Sticky footer cell background via ::before
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            // Maintain stickiness & layer above ::before
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: 'white',
        // Fill the card so the totals footer sits at the bottom instead of leaving dead space.
        ...(fullHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
      },
    },
    ...(fullHeight
      ? {
          muiTableProps: {
            sx: {
              ...((commonMRTStyling.muiTableProps as any)?.sx ?? {}),
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }
      : {}),
    muiTableContainerProps: {
      sx: {
        padding: '0px',
        maxHeight: fullHeight ? 'none' : isMagicReport ? '550px' : '400px',
        ...(fullHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
        // Hide the gray empty row
        '& .MuiBox-root.css-kd8zx9': {
          display: 'none !important',
        },
        // Override MRT's pinned cell alignment to center
        '& .css-1jh1yab-MuiTableCell-root[data-pinned="true"]': {
          justifyContent: 'center !important',
        },
        '& .css-1evnjx0-MuiTableCell-root': {
          justifyContent: 'center !important',
        },
        // Ensure table body has enough height to push footer down
        '& .MuiTableBody-root': {
          minHeight: '100px',
          display: 'unset',
          ...(fullHeight ? { flex: 1 } : {}),
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        padding: '10px 12px',
        border: 'none',
        fontSize: '0.8rem',
        // MRT only allows wrapping for headers >= 20 chars (shorter ones get nowrap + ellipsis),
        // so short headers are forced to wrap here rather than truncate in a narrow column.
        '& .Mui-TableHeadCell-Content-Wrapper': {
          fontSize: '0.8rem',
          whiteSpace: 'normal',
          overflow: 'visible',
          textOverflow: 'clip',
        },
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme => theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
      }),
    },
    muiTableBodyCellProps: ({ row }) => {
      const rowBg = getRowBackground(row.original, row.index);

      return {
        sx: {
          border: 'none',
          background: `${rowBg} !important`,
        },
      };
    },
    muiTableBodyRowProps: ({ row }) => {
      const fundingId = row.original.id;
      const rowBg = getRowBackground(row.original, row.index);

      return {
        onMouseEnter: () => onHoveredFundingId?.(fundingId),
        onMouseLeave: () => onHoveredFundingId?.(null),
        sx: {
          background: `${rowBg} !important`,
          '&:hover': {
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
          // Also apply the same background to sticky pseudo-elements in this row
          '& td[data-pinned="true"]::before': {
            background: rowBg,
          },
        },
      };
    },
  });

  if (!tableData.length && readonly) {
    return null;
  }

  return (
    <>
      <Dialog open={isAddDialogOpen} onClose={handleCloseAddEntryDialog} fullWidth maxWidth='sm'>
        <DialogTitle>Add Previous Funding Entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={newFundingForm.isAdjustment} onChange={(_, checked) => handleAdjustmentToggle(checked)} />}
              label='Adjustment'
            />
            <TextField
              label='Application #'
              type='text'
              fullWidth
              value={newFundingForm.applicationNumber}
              onChange={event =>
                setNewFundingForm(prev => ({
                  ...prev,
                  applicationNumber: event.target.value,
                }))
              }
            />
            <DynamicDatePicker
              fieldName='applicationDate'
              label='Application Date (optional)'
              value={newFundingForm.applicationDate}
              onChange={(_, value) =>
                setNewFundingForm(prev => ({
                  ...prev,
                  applicationDate: value,
                }))
              }
            />
            <TextField
              label='Received by Contractor'
              type='number'
              fullWidth
              required={newFundingForm.isAdjustment}
              inputProps={{ step: 0.01 }}
              value={newFundingForm.receivedByContractor}
              onChange={event =>
                setNewFundingForm(prev => ({
                  ...prev,
                  receivedByContractor: event.target.value,
                }))
              }
            />
            <TextField
              label='Funded by Lender'
              type='number'
              fullWidth
              disabled={newFundingForm.isAdjustment}
              helperText={newFundingForm.isAdjustment ? 'Cannot be filled for adjustments' : undefined}
              inputProps={{ step: 0.01 }}
              value={newFundingForm.amountFunded}
              onChange={event =>
                setNewFundingForm(prev => ({
                  ...prev,
                  amountFunded: event.target.value,
                }))
              }
            />
            <TextField
              label='Internal Comment (optional)'
              type='text'
              fullWidth
              multiline
              rows={2}
              value={newFundingForm.comment}
              onChange={event =>
                setNewFundingForm(prev => ({
                  ...prev,
                  comment: event.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddEntryDialog} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddEntry}
            variant='contained'
            disabled={isAddEntryDisabled}
            sx={{
              color: isAddEntryDisabled ? undefined : theme.palette.common.white,
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      {isMagicReport ? (
        <Box>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='body2' color='text.secondary' fontWeight={500} sx={{ letterSpacing: 1 }}>
              Summary of Previous Fundings
            </Typography>
            <Tooltip title='Add previous funding entry' arrow={false}>
              <Box>
                <WhiteFab
                  size='small'
                  icon={<AddIcon fontSize='small' />}
                  onClick={openAddEntryDialog}
                  textColor='primary'
                  disabled={!allowEditing}
                />
              </Box>
            </Tooltip>
          </Box>
          <MaterialReactTable table={table} />
        </Box>
      ) : (
        <Card
          elevation={3}
          sx={{ p: 2, my: 1, ...(fullHeight ? { flex: 1, width: '100%', display: 'flex', flexDirection: 'column' } : {}) }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box display='flex' alignItems='center'>
              <DollarIcon color='primary' sx={{ fontSize: '24px !important', fontWeight: '700 !important' }} />
              <Typography variant='h5' color='primary' sx={{ ml: 0.5, mb: 0 }}>
                Summary of Previous Fundings
              </Typography>
            </Box>
            <Tooltip title='Add previous funding entry' arrow={false}>
              <Box>
                <WhiteFab
                  size='small'
                  icon={<AddIcon fontSize='small' />}
                  onClick={openAddEntryDialog}
                  textColor='primary'
                  disabled={!allowEditing}
                />
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 2, ...(fullHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
            <MaterialReactTable table={table} />
          </Box>
        </Card>
      )}
    </>
  );
};

export default ProjectPreviousFundingsTable;
